import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export default function PlanChangeResult({ onComplete }) {
  const [params] = useSearchParams();
  const sessionId = params.get("plan_change_session_id");
  const cancelledAttempt = params.get("plan_change_cancelled");
  const [message, setMessage] = useState("");
  const [retry, setRetry] = useState(0);
  const [canRetry, setCanRetry] = useState(false);

  useEffect(() => {
    if (!sessionId && !cancelledAttempt) return undefined;
    const controller = new AbortController();
    let timer;
    let attempts = 0;
    setCanRetry(false);
    setMessage(sessionId ? "Confirming your payment and activating your new plan..." : "Closing checkout...");
    const check = async () => {
      try {
        const response = await fetch(sessionId
          ? `/api/plan-change-status?session_id=${encodeURIComponent(sessionId)}`
          : `/api/plan-change-checkout/${encodeURIComponent(cancelledAttempt)}`, {
          credentials: "include", signal: controller.signal,
          method: sessionId ? "GET" : "DELETE",
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Unable to confirm your plan change. Please retry confirmation.");
        if (data.status === "completed") {
          setMessage("Payment confirmed. Your previous plan has ended and your new plan is active. You can manage or cancel its renewal below.");
          onComplete();
        } else if (["cancelled", "expired"].includes(data.status)) {
          setMessage("Checkout closed. Your previous plan was not changed.");
        } else if (++attempts < 30) {
          timer = setTimeout(check, 2000);
        } else {
          setMessage("Your payment is still being confirmed. Please do not pay again. You can retry confirmation here.");
          setCanRetry(true);
        }
      } catch (error) {
        if (error.name === "AbortError") return;
        setMessage(error.message);
        setCanRetry(true);
      }
    };
    check();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [sessionId, cancelledAttempt, retry, onComplete]);

  if (!message) return null;
  return (
    <div role="status" className="mb-5 rounded-md border border-purple-200 bg-purple-50 p-4 text-sm text-purple-900">
      <p>{message}</p>
      {canRetry && <button type="button" onClick={() => setRetry((value) => value + 1)} className="mt-3 rounded-md bg-purple-700 px-3 py-2 text-xs font-bold text-white hover:bg-purple-800">Retry confirmation</button>}
    </div>
  );
}
